import { StaticModal } from '@nexelem/modals';
import { Button } from '@nexelem/components';
import { closeSimulationEditForm, SimulationFormData } from '../..';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { openRecalculateModal } from '../../..';
import { useFormContext, useFormState } from 'react-hook-form';
import { faFlag } from '@fortawesome/pro-solid-svg-icons';
import type { SimulationTasksUpdates, SingleSimulationTaskUpdate } from '@nexelem/graphne-api';
import { useCustomTranslation } from '../../../../../../../../hooks/useCustomTranslation';

type SimulationEditFooterProps = {
  taskNumber: number;
};

export const SimulationEditFooter = ({ taskNumber }: SimulationEditFooterProps) => {
  const { tWrapper } = useCustomTranslation('products.simulationPlan');
  const { control, getValues, formState } = useFormContext<SimulationFormData>();
  const { errors } = useFormState({ control });

  const hasErrors = Object.keys(errors).length > 0;
  const hasChanges = Object.keys(formState.dirtyFields).length > 0;

  const collectEditChangesFromForm = (): SimulationTasksUpdates => {
    const taskUpdatesById = new Map<number, SingleSimulationTaskUpdate>();

    const getOrCreateTaskUpdate = (taskId: number) => {
      let existingUpdate = taskUpdatesById.get(taskId);
      if (!existingUpdate) {
        existingUpdate = { taskId, unplan: false };
        taskUpdatesById.set(taskId, existingUpdate);
      }
      return existingUpdate;
    };

    const currentFormValues = getValues();
    const dirtyFields = formState.dirtyFields || {};

    for (const taskIdKey of Object.keys(dirtyFields)) {
      const taskId = Number.parseInt(taskIdKey, 10);
      const taskFormValues = currentFormValues[taskId];
      const dirtyTaskFields = dirtyFields[taskId];
      if (!dirtyTaskFields) {
        continue;
      }

      if (dirtyTaskFields.amount && taskFormValues && !taskFormValues.delete) {
        getOrCreateTaskUpdate(taskId).amount = taskFormValues.amount;
      }
      if (dirtyTaskFields.technology && taskFormValues?.technology && !taskFormValues.delete) {
        getOrCreateTaskUpdate(taskId).recipeVersionName = taskFormValues.technology;
      }
      if (dirtyTaskFields.priority && taskFormValues && !taskFormValues.delete) {
        getOrCreateTaskUpdate(taskId).priority = taskFormValues.priority;
      }
      if (dirtyTaskFields.delete && taskFormValues?.delete === true) {
        taskUpdatesById.set(taskId, { taskId, unplan: true });
      }
    }

    return { tasks: [...taskUpdatesById.values()] };
  };

  const handleSaveAndReturn = () => {
    openRecalculateModal({ simulationTasksUpdates: collectEditChangesFromForm() });
  };

  return (
    <StaticModal.Footer className="flex items-center justify-between border-t border-border-primary px-8 py-2">
      <div className="text-s11-sb uppercase">
        {taskNumber} {tWrapper('footerTotal')}
      </div>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon className="min-h-5 w-5 text-icon-brand" icon={faFlag} />
          <span className="text-s16">{tWrapper('footerPriority')}</span>
        </div>
        {hasErrors && (
          <div className="flex items-center gap-2">
            <FontAwesomeIcon className="min-h-5 w-5 text-icon-status-orange-lighter" icon={faExclamationTriangle} />
            <span className="text-s16">{tWrapper('footerInconsistentAmount')}</span>
          </div>
        )}
      </div>
      <div className="flex gap-4">
        <Button variant="outlined" onClick={closeSimulationEditForm}>
          {tWrapper('simulationCancel')}
        </Button>
        <Button disabled={hasErrors || !hasChanges} variant="contained" onClick={handleSaveAndReturn}>
          {tWrapper('footerSaveAndReturn')}
        </Button>
      </div>
    </StaticModal.Footer>
  );
};
